import HomeIcon from '@material-design-icons/svg/filled/home.svg?react';
import PublishIcon from '@material-design-icons/svg/filled/publish.svg?react';
import DeleteIcon from '@material-design-icons/svg/filled/delete.svg?react';
import TokenIcon from '@material-design-icons/svg/filled/token.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import type { Dapp } from '@src/dto';
import type { Release } from '@src/dto/Release';
import { isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import { useAsyncData } from '../../hooks/useAsyncData';
import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { useSidebarData } from '../sidebar/hooks/useSidebarData';
import { captureAndReportError } from '../../utils/errorReporting';
import { hasSolanaEmailAddress } from '../../utils/roleUtils';
import { getNewVersionBlockedMessage } from '../../utils/publicationAccess';
import { PUBLISHER_FROZEN_ACTION_TOOLTIP } from '../../utils/publisherFreeze';
import { getPublisherVerificationGate } from '../../utils/publisherVerification';
import {
  getDappNewVersionPath,
  getDappReleasePath,
} from '../../utils/dappRoutes';
import { AppNftMintModal } from '../AppNftMintModal';
import { useOptionalDappContext } from '../DappLayout';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { AppCollectionVerificationPrompt } from './AppCollectionVerificationPrompt';
import { DashboardHeader } from './DashboardHeader';
import { DashboardReleasesTable } from './DashboardReleasesTable';
import { DeleteReleaseDialog } from './DeleteReleaseDialog';
import { DeleteDraftDappDialog } from './DeleteDraftDappDialog';

type DappWithPublisherAddresses = Dapp & {
  publisherWalletAddress?: string | null;
  publisherNftMintAddress?: string | null;
};

export const DashboardHome = () => {
  const dappRoute = useOptionalDappContext();
  const dappId = dappRoute?.dappId;
  const dappRouteDapp = dappId
    ? { id: dappId, packageName: dappRoute.packageName }
    : null;
  const navigate = useNavigate();
  const trpc = useTRPC();
  const wallet = useWallet();
  const [authState] = useAuth();
  const [dappName, setDappName] = useState<string>('Loading...');
  const {
    data: releases,
    setData: setReleases,
    isLoading: releasesLoading,
    setIsLoading: setReleasesLoading,
    error: releasesError,
    setError: setReleasesError,
    run: runReleases,
  } = useAsyncData<Release[]>({
    initialData: [],
    initialLoading: true,
  });
  const [showAppNftModal, setShowAppNftModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [releasePendingDelete, setReleasePendingDelete] =
    useState<Release | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeletingRelease, setIsDeletingRelease] = useState(false);
  const [isDeleteAppOpen, setIsDeleteAppOpen] = useState(false);
  const [isDeletingApp, setIsDeletingApp] = useState(false);
  const [isAppCollectionVerified, setIsAppCollectionVerified] = useState<
    boolean | null
  >(null);
  const {
    data: appData,
    setData: setAppData,
    run: runAppData,
  } = useAsyncData<{
    id: string;
    name: string;
    iconUrl?: string;
    website?: string;
    androidPackage: string;
    publisherAddress: string;
    hasAppNft: boolean;
    nftMintAddress: string | null;
    canManageReleases?: boolean;
    canSubmitToStore?: boolean;
    canManageNfts?: boolean;
    publisherFrozen?: boolean;
    walletAddress?: string;
    publisherWalletAddress?: string;
    publisherNftMintAddress?: string;
    lastApprovedReleaseId?: string | null;
    hasApprovedRelease?: boolean;
    status: Dapp['status'];
    canManageDapps?: boolean;
  } | null>({
    initialData: null,
  });
  const { dappData } = useDappDataOptional();
  const { activePublisher } = usePublisher();
  const { removeDapp } = useSidebarData();
  const contextDappName = dappData?.dappName || null;
  const isSuperadmin =
    authState.type === AuthStateType.Authenticated &&
    authState.user.role === 'superadmin' &&
    hasSolanaEmailAddress(authState.user.email);
  const showActionsColumn = isSuperadmin;

  const displayName = contextDappName || dappName;
  usePageTitle(
    dappId && displayName !== 'Loading...'
      ? `${displayName} - Home | Publishing Portal`
      : 'Home | Publishing Portal',
  );

  const syncAppData = (dapp: DappWithPublisherAddresses) => {
    setDappName(dapp.dappName);
    setAppData({
      id: dapp.id,
      name: dapp.dappName,
      iconUrl: dapp.dappIconUrl || undefined,
      website: dapp.appWebsite || undefined,
      androidPackage: dapp.packageName || 'com.example.app',
      publisherAddress: dapp.walletAddress || '',
      hasAppNft: !!dapp.nftMintAddress,
      nftMintAddress: dapp.nftMintAddress,
      canManageReleases: dapp.canManageReleases,
      canSubmitToStore: dapp.canSubmitToStore,
      canManageNfts: dapp.canManageNfts,
      publisherFrozen: dapp.publisherFrozen,
      walletAddress: dapp.walletAddress || undefined,
      publisherWalletAddress: dapp.publisherWalletAddress || undefined,
      publisherNftMintAddress: dapp.publisherNftMintAddress || undefined,
      lastApprovedReleaseId: dapp.lastApprovedReleaseId || null,
      hasApprovedRelease: dapp.hasApprovedRelease ?? false,
      status: dapp.status,
      canManageDapps: dapp.canManageDapps,
    });
  };

  const handleDeleteDraftApp = async () => {
    if (!dappId || appData?.status !== 'Draft') return;

    setIsDeletingApp(true);
    try {
      const result = await trpc.deleteDraftDapp.mutate({ dappId });
      if (result._tag === 'Left') {
        toast.error(result.left.message);
        return;
      }

      removeDapp(dappId);
      toast.success(result.right.message);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      captureAndReportError(error, {
        operation: 'deleteDraftDapp',
        component: 'DashboardHome',
        dappId,
      });
      toast.error('Failed to delete draft app');
    } finally {
      setIsDeletingApp(false);
    }
  };

  const closeDeleteReleaseDialog = () => {
    if (isDeletingRelease) {
      return;
    }

    setReleasePendingDelete(null);
    setDeleteReason('');
  };

  const handleDeleteRelease = async () => {
    if (!releasePendingDelete || releasePendingDelete.source !== 'Portal') {
      return;
    }

    const releaseToDelete = releasePendingDelete;

    setIsDeletingRelease(true);
    try {
      const result = await trpc.release.deleteRelease.mutate({
        releaseId: releaseToDelete.id,
        reason: deleteReason.trim() || undefined,
      });

      if (result._tag === 'Left') {
        toast.error(result.left.message);
        return;
      }

      setReleases((prev) =>
        prev.filter((release) => release.id !== releaseToDelete.id),
      );
      setReleasePendingDelete(null);
      setDeleteReason('');
      toast.success('Release deleted successfully');

      if (dappId) {
        try {
          const dappResult = await trpc.getDapp.query({ dappId });
          if (isRight(dappResult)) {
            syncAppData(dappResult.right);
          }
        } catch (error) {
          captureAndReportError(error, {
            operation: 'dashboard.refreshDappAfterReleaseDelete',
            component: 'DashboardHome',
            dappId,
            releaseId: releaseToDelete.id,
          });
        }
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'release.deleteRelease',
        component: 'DashboardHome',
        dappId,
        releaseId: releaseToDelete.id,
      });
      toast.error('Failed to delete release');
    } finally {
      setIsDeletingRelease(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [releases.length]);

  useEffect(() => {
    if (
      !dappId ||
      appData?.canManageNfts !== true ||
      !appData.nftMintAddress ||
      !appData.publisherNftMintAddress
    ) {
      setIsAppCollectionVerified(null);
      return;
    }

    let isCancelled = false;

    const checkAppCollectionStatus = async () => {
      try {
        const result = await trpc.checkOnChainAppCollectionStatus.query({
          dappId,
        });

        if (isCancelled || result._tag === 'Left') {
          return;
        }

        setIsAppCollectionVerified(result.right.isVerifiedOnChain);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setIsAppCollectionVerified(null);
        captureAndReportError(error, {
          operation: 'dashboard.checkOnChainAppCollectionStatus',
          component: 'DashboardHome',
          dappId,
        });
      }
    };

    void checkAppCollectionStatus();

    return () => {
      isCancelled = true;
    };
  }, [
    appData?.canManageNfts,
    appData?.nftMintAddress,
    appData?.publisherNftMintAddress,
    dappId,
    trpc,
  ]);

  const isPublisherFrozen =
    activePublisher?.isFrozen === true || appData?.publisherFrozen === true;
  const publisherVerification = getPublisherVerificationGate({
    status: activePublisher?.kybStatus,
    publisherType: activePublisher?.publisherType,
    kycKybBypassEnabled: activePublisher?.kycKybBypassEnabled,
  });

  const shouldNewVersionButtonBeEnabled = () => {
    if (isPublisherFrozen) return false;

    if (!publisherVerification.isApproved) return false;

    if (!wallet.connected) return false;

    if (
      appData?.walletAddress &&
      wallet.publicKey &&
      appData.walletAddress !== wallet.publicKey.toBase58()
    ) {
      return false;
    }

    if (getNewVersionBlockedMessage(appData)) return false;

    return true;
  };

  const getNewVersionButtonTooltip = () => {
    if (isPublisherFrozen) {
      return PUBLISHER_FROZEN_ACTION_TOOLTIP;
    }

    if (!publisherVerification.isApproved) {
      return publisherVerification.newVersionMessage;
    }

    if (!wallet.connected) {
      return 'Connect a wallet to create a new version';
    }

    if (
      appData?.walletAddress &&
      wallet.publicKey &&
      appData.walletAddress !== wallet.publicKey.toBase58()
    ) {
      return `Connect the wallet that minted the App NFT (${appData.walletAddress.slice(0, 4)}...${appData.walletAddress.slice(-4)})`;
    }

    const blockedMessage = getNewVersionBlockedMessage(appData);
    if (blockedMessage) {
      return blockedMessage;
    }

    return 'Create a new version of your app';
  };

  useEffect(() => {
    if (dappId && authState.type === AuthStateType.Authenticated) {
      const timer = setTimeout(() => {
        void runAppData(() => trpc.getDapp.query({ dappId }), {
          onSuccess: (dappsResult) => {
            if (
              dappsResult &&
              typeof dappsResult === 'object' &&
              '_tag' in dappsResult
            ) {
              if (isRight(dappsResult)) {
                syncAppData(dappsResult.right);
              }
            }
          },
          onError: (err) => {
            captureAndReportError('Failed to fetch dapp details:', err);
          },
        });

        void runReleases(() => trpc.release.listReleases.query({ dappId }), {
          showLoadingState: false,
          clearError: false,
          onSuccess: (result, helpers) => {
            if (result && typeof result === 'object' && '_tag' in result) {
              if (isRight(result)) {
                helpers.setData(result.right);
              } else {
                helpers.setError('Failed to load releases');
              }
            }
            helpers.setIsLoading(false);
          },
          onError: (err, helpers) => {
            captureAndReportError('Failed to fetch releases:', err);
            helpers.setError('Failed to load releases');
            helpers.setIsLoading(false);
          },
        });
      }, 100);

      return () => clearTimeout(timer);
    } else if (authState.type === AuthStateType.NoToken) {
      setReleasesError('Authentication required');
      setReleasesLoading(false);
    }
  }, [
    dappId,
    authState.type,
    runAppData,
    runReleases,
    setReleasesError,
    setReleasesLoading,
    trpc,
  ]);

  return (
    <div className="flex flex-col h-full bg-[#161717]">
      <DashboardHeader>
        <div className="flex items-center gap-2">
          <HomeIcon className="size-5 fill-white" />
          <span className="text-white font-medium text-base">Home</span>
        </div>
        <div className="flex items-center gap-3">
          {appData?.status === 'Draft' && appData.canManageDapps && (
            <button
              type="button"
              onClick={() => setIsDeleteAppOpen(true)}
              className="font-semibold text-sm px-4 py-2 rounded-lg flex items-center gap-2 border border-red-500/40 text-red-300 transition-colors hover:bg-red-500/10"
            >
              <DeleteIcon className="size-4 fill-current" />
              Delete draft
            </button>
          )}
          {appData && !appData.hasAppNft && appData.publisherAddress && (
            <button
              type="button"
              onClick={() => setShowAppNftModal(true)}
              className="font-semibold text-sm px-4 py-2 rounded-lg flex items-center gap-2 transition-colors bg-slate-600 text-white hover:bg-slate-700"
            >
              <TokenIcon className="size-4 fill-white" />
              Mint App
            </button>
          )}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <button
                    type="button"
                    onClick={() => {
                      if (dappRouteDapp) {
                        navigate(getDappNewVersionPath(dappRouteDapp));
                      }
                    }}
                    disabled={!shouldNewVersionButtonBeEnabled()}
                    className={`font-semibold text-sm px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                      !shouldNewVersionButtonBeEnabled()
                        ? 'bg-neutral-700 text-neutral-400 cursor-not-allowed pointer-events-none'
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                  >
                    <PublishIcon className="size-4 fill-current" />
                    New Version
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{getNewVersionButtonTooltip()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </DashboardHeader>

      <div className="flex-1 p-6 overflow-hidden flex flex-col">
        <AppCollectionVerificationPrompt
          appData={appData}
          isAppCollectionVerified={isAppCollectionVerified}
          onVerificationSuccess={() => setIsAppCollectionVerified(true)}
        />

        {authState.type === AuthStateType.Verifying ? (
          <div className="bg-[#161717] rounded-xl p-8 text-center">
            <span className="text-neutral-400">Verifying authentication…</span>
          </div>
        ) : releasesLoading ? (
          <div className="bg-[#161717] rounded-xl p-8 text-center">
            <span className="text-neutral-400">Loading releases…</span>
          </div>
        ) : releasesError ? (
          <div className="bg-[#161717] rounded-xl p-8 text-center">
            <span className="text-red-500">{releasesError}</span>
          </div>
        ) : releases.length === 0 ? (
          <div className="bg-[#161717] rounded-xl p-8 text-center">
            <span className="text-neutral-400">
              No releases yet. Create your first release to get started.
            </span>
          </div>
        ) : (
          <DashboardReleasesTable
            releases={releases}
            currentPage={currentPage}
            showActionsColumn={showActionsColumn}
            onPageChange={setCurrentPage}
            onOpenRelease={(release) => {
              if (release.source !== 'Hubspot' && dappRouteDapp) {
                navigate(getDappReleasePath(dappRouteDapp, release.id));
              }
            }}
            onRequestDeleteRelease={setReleasePendingDelete}
          />
        )}
      </div>

      {appData && (
        <AppNftMintModal
          isOpen={showAppNftModal}
          onClose={() => setShowAppNftModal(false)}
          onSuccess={(mintAddress, txSignature) => {
            console.log('App NFT minted successfully:', {
              mintAddress,
              txSignature,
            });
            setShowAppNftModal(false);
            toast.success('App NFT minted successfully!');
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }}
          appData={appData}
        />
      )}

      <DeleteReleaseDialog
        releasePendingDelete={releasePendingDelete}
        deleteReason={deleteReason}
        isDeletingRelease={isDeletingRelease}
        onClose={closeDeleteReleaseDialog}
        onDelete={handleDeleteRelease}
        onDeleteReasonChange={setDeleteReason}
      />
      <DeleteDraftDappDialog
        dappName={appData?.name ?? ''}
        isOpen={isDeleteAppOpen}
        isDeleting={isDeletingApp}
        onClose={() => setIsDeleteAppOpen(false)}
        onDelete={handleDeleteDraftApp}
      />
    </div>
  );
};
